import React from 'react';
import styled from 'styled-components';
import { Box, Heading, Stack, Text } from '@99designs/emu';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { BodyText, TileSelect } from '@99designs/spa-common';
import { useOnboarder } from '../../components/Onboarder/OnboarderContext';
import { Price } from '../../components/Price';
import { Answer } from '../../components/Step/Answer';
import { budgetRangesInCents } from './BudgetRanges';
import { ReactComponent as MoneyBag1 } from './money-bag-1.svg';
import { ReactComponent as MoneyBag2 } from './money-bag-2.svg';
import { ReactComponent as MoneyBag3 } from './money-bag-3.svg';
import type { CurrencyCode } from '@99designs/i18n';
import type { StepComponentProps, StepHeadingProps } from '../../components/Onboarder/Config';
import type { Data } from '../config';

export type BudgetValue = 'low' | 'moderate' | 'high';

const MoneyBagIcon = styled.div<{ width: number }>`
    width: ${({ width }) => width}px;

    svg {
        display: inline-block;
        width: inherit;
        height: inherit;
    }
`;

const StyledHeading = styled(Heading)`
    text-align: center;
`;

function BudgetPrice({ amount, currency }: { amount?: number; currency: CurrencyCode }) {
    // eslint-disable-next-line eqeqeq
    if (amount == undefined) {
        return null;
    }
    return <Price amountInCents={amount} currency={currency} />;
}

export function BudgetStepInputs({ data, onChange }: StepComponentProps<Data>) {
    const { config } = useOnboarder();
    const { currency } = config;

    const lowBudget = budgetRangesInCents[currency].low;
    const moderateBudget = budgetRangesInCents[currency].moderate;
    const highBudget = budgetRangesInCents[currency].high;

    return (
        <Stack space="2bu">
            <TileSelect<BudgetValue>
                name="budget"
                value={data.budget}
                options={[
                    {
                        label: (
                            <BudgetDetails
                                icon={
                                    <MoneyBagIcon width={70}>
                                        <MoneyBag1 />
                                    </MoneyBagIcon>
                                }
                                heading={
                                    <FormattedMessage
                                        message={__('Low ({fromPrice} to {toPrice})')}
                                        values={{
                                            fromPrice: (
                                                <BudgetPrice
                                                    amount={lowBudget.fromPrice}
                                                    currency={currency}
                                                />
                                            ),
                                            toPrice: (
                                                <BudgetPrice
                                                    amount={lowBudget.toPrice}
                                                    currency={currency}
                                                />
                                            ),
                                        }}
                                    />
                                }
                                body={__(
                                    'Simple designs from entry level designers, using a budget web builder.'
                                )}
                            />
                        ),
                        value: 'low',
                    },
                    {
                        label: (
                            <BudgetDetails
                                icon={
                                    <MoneyBagIcon width={130}>
                                        <MoneyBag2 />
                                    </MoneyBagIcon>
                                }
                                heading={
                                    <FormattedMessage
                                        message={__('Moderate ({fromPrice} to {toPrice})')}
                                        values={{
                                            fromPrice: (
                                                <BudgetPrice
                                                    amount={moderateBudget.fromPrice}
                                                    currency={currency}
                                                />
                                            ),
                                            toPrice: (
                                                <BudgetPrice
                                                    amount={moderateBudget.toPrice}
                                                    currency={currency}
                                                />
                                            ),
                                        }}
                                    />
                                }
                                body={__(
                                    'Considered designs from mid level designers, using a customizable web builder.'
                                )}
                            />
                        ),
                        value: 'moderate',
                    },
                    {
                        label: (
                            <BudgetDetails
                                icon={
                                    <MoneyBagIcon width={160}>
                                        <MoneyBag3 />
                                    </MoneyBagIcon>
                                }
                                heading={
                                    <FormattedMessage
                                        message={__('High ({fromPrice}+)')}
                                        values={{
                                            fromPrice: (
                                                <BudgetPrice
                                                    amount={highBudget.fromPrice}
                                                    currency={currency}
                                                />
                                            ),
                                        }}
                                    />
                                }
                                body={__(
                                    'Complex designs from top level designers, using a highly customizable web builder.'
                                )}
                            />
                        ),
                        value: 'high',
                    },
                ]}
                onChange={(value, name) => {
                    const newData: Data = {
                        ...data,
                        [name]: value,
                    };
                    onChange(newData);
                }}
            />
            <Text color="grey700" size="size2">
                <strong>{__('Please note')}:</strong>{' '}
                {__('Upfront costs only. Ongoing, third-party costs not included.')}
            </Text>
        </Stack>
    );
}

interface BudgetDetailsProps {
    icon: React.ReactNode;
    heading: React.ReactNode;
    body: string;
}

function BudgetDetails({ icon, heading, body }: BudgetDetailsProps) {
    return (
        <Box
            padding={['2bu', '2bu', '2bu', '3bu']}
            display="flex"
            flexDirection="column"
            alignItems="center"
        >
            <Box height="105px" display="flex" alignItems="flex-end" marginBottom="0.5bu">
                {icon}
            </Box>
            <StyledHeading appearanceLevel={5} accessibilityLevel={4}>
                {heading}
            </StyledHeading>
            <BodyText>{body}</BodyText>
        </Box>
    );
}

const budgetRangeAnswerCopies = (): { [key: string]: string } => ({
    low: __('low ({fromPrice} to {toPrice})'),
    moderate: __('moderate ({fromPrice} to {toPrice})'),
    high: __('high ({fromPrice}+)'),
});

export function answerCopy(budget: string, currency: CurrencyCode): React.ReactNode {
    const budgetCopy = budgetRangeAnswerCopies()[budget];
    if (!budgetCopy) {
        return null;
    }

    const budgetRange = budgetRangesInCents[currency][budget];

    return (
        <FormattedMessage
            message={budgetCopy}
            values={{
                fromPrice: <BudgetPrice amount={budgetRange.fromPrice} currency={currency} />,
                toPrice: budgetRange && (
                    <BudgetPrice amount={budgetRange.toPrice} currency={currency} />
                ),
            }}
        />
    );
}

export const questionCopy = () => __('My budget for this website is {answer}');

export function BudgetStepHeading({ data }: StepHeadingProps<Data>) {
    const { config } = useOnboarder();
    const answer = data.budget ? answerCopy(data.budget, config.currency) : '';

    return (
        <FormattedMessage message={questionCopy()} values={{ answer: <Answer text={answer} /> }} />
    );
}
